import { event as gTagEvent } from 'vue-gtag'
import loggerMixin from '@/mixins/logger'
import { EVENT_PROVIDERS, ANALYTICS_EVENTS } from '@/util/constants'
import { version } from '../../package.json'
import Util from '@/util'

/**
 * Enriches payload with client metadata (version, environment, browser info)
 * @param {Object} payload - Original event payload
 * @returns {Object} Enriched payload with client metadata
 */
function enrichPayloadWithClientMetadata(payload = {}) {
  const browserInfo = typeof window !== 'undefined' ? Util.getDeviceAndBrowserInfo() : {}
  return {
    ...payload,
    clientVersion: version,
    env: process.env.NODE_ENV || 'unknown',
    browser: browserInfo.browser || 'unknown',
    device: browserInfo.device || 'unknown',
    os: browserInfo.os || 'unknown',
    engine: browserInfo.engine || 'unknown',
  }
}

async function sendToLogzio(payload = {}, store) {
  if (!store) return 
  const profile = store && store.getters && store.getters.getUserProfile
    ? store.getters.getUserProfile
    : null
  
  if (!profile || !profile.id) {
    return
  }
  let token = store.getters && typeof store.getters.getConfig === 'function' && store.getters.getConfig('LOGZIO_TOKEN')
  if (!token && typeof store.dispatch === 'function') {
    try {
      await store.dispatch('fetchConfig')
      token = store.getters && typeof store.getters.getConfig === 'function' && store.getters.getConfig('LOGZIO_TOKEN')
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[eventTransmitter] Error fetching config:', e)
      }
    }
  }
  if (!token) return
  const url = `https://listener.logz.io:8071/?token=${encodeURIComponent(token)}&type=frontend`
  try {
    const company = store && store.getters && store.getters.getSelectedCompany
      ? store.getters.getSelectedCompany
      : null

    const derivedUser = {
      id: profile && profile.id,
      email: profile && profile.email,
      name: profile && ((profile.firstName || '') + ' ' + (profile.lastName || '')).trim() || undefined,
      company: company && company.name,
    }

    // Start with base fields, then apply payload, then enrich user fields if missing
    const base = {
      type: 'frontend',
      logType: 'vuejs',
      via: 'client',
      timestamp: new Date().toISOString(),
    }

    const merged = { ...base, ...payload }
    const existingUser = (merged && typeof merged.user === 'object') ? merged.user : {}
    const user = {
      id: existingUser.id || derivedUser.id,
      email: existingUser.email || derivedUser.email,
      name: existingUser.name || derivedUser.name,
      company: existingUser.company || derivedUser.company,
    }
    // Attach top-level aliases to simplify Logz queries
    if (user.email && !merged.user_email) merged.user_email = user.email
    if (user.id && !merged.user_id) merged.user_id = user.id
    if (user.company && !merged.user_company) merged.user_company = user.company
    if (company && company.name && !merged.company_name) merged.company_name = company.name

    const finalPayload = { ...merged, user }
    const body = JSON.stringify(finalPayload)
    // Prefer beacon to avoid CORS preflight and ensure delivery on unload
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' })
      navigator.sendBeacon(url, blob)
    } else {
      // Fallback: no custom headers, no-cors to avoid preflight
      await fetch(url, {
        method: 'POST',
        body,
        mode: 'no-cors',
        keepalive: true,
      })
    }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[eventTransmitter] Error sending to Logz.io:', e)
      }
    }
}

/**
 * Validates that eventName is from ANALYTICS_EVENTS constants
 * @param {string} eventName - Event name to validate
 * @throws {Error} If eventName is not a valid ANALYTICS_EVENTS constant
 */
function validateEventName(eventName) {
  if (!eventName) {
    const errorMessage = 'eventName is required and must be from ANALYTICS_EVENTS constants'
    if (process.env.NODE_ENV === 'development') {
      console.error('[eventTransmitter]', errorMessage)
      throw new Error(errorMessage)
    }
    return
  }
  
  const validEventNames = Object.values(ANALYTICS_EVENTS)
  if (!validEventNames.includes(eventName)) {
    const errorMessage = `Invalid eventName "${eventName}". Must be one of: ${validEventNames.join(', ')}`
    if (process.env.NODE_ENV === 'development') {
      console.error('[eventTransmitter]', errorMessage)
      throw new Error(errorMessage)
    }
    // In production, silently fail to avoid breaking the app
  }
}

export async function sendEvent(providers = [], payload = {}, store) {
  if (!Array.isArray(providers)) providers = [providers]
  
  // Always validate eventName if present (enforce constants for all providers)
  if (payload && payload.eventName) {
    validateEventName(payload.eventName)
  }

  
  // Use for...of to properly handle async operations
  for (const provider of providers) {
    try {
      switch (provider) {
        case EVENT_PROVIDERS.GA: {
          const { eventName, ...params } = payload || {}
          if (eventName) {
            try {
              // Check if gtag is available and initialized
              if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                // Use window.gtag directly - this is the most reliable method
                // Ensure we're calling it with the correct signature for GA4
                window.gtag('event', eventName, params)
              } else if (typeof gTagEvent === 'function') {
                // Fallback to vue-gtag's event function
                gTagEvent(eventName, params)
              } else {
                if (process.env.NODE_ENV === 'development') {
                  console.warn('[eventTransmitter] gtag not initialized, event not sent:', eventName)
                }
              }
            } catch (e) {
              if (process.env.NODE_ENV === 'development') {
                console.error('[eventTransmitter] Error sending GA event:', e)
              }
            }
          }
          break
        }
        case EVENT_PROVIDERS.LOGZIO: {
          // Enrich payload with client metadata (version, env, browser info)
          const enrichedPayload = enrichPayloadWithClientMetadata(payload)
          // If used outside components, rely on provided store
          // For components, mixin passes this.$store
          await sendToLogzio(enrichedPayload, store)
          break
        }
        default:
          break
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error(`[eventTransmitter] Error sending to ${provider}:`, e)
      }
    }
  }
}

export default {
  mixins: [loggerMixin],
  methods: {
    /**
     * Transmit an analytics event to one or more providers.
     *
     * @param {Array<string>} providers - Array of provider identifiers from PROVIDERS.
     * @param {Object} payload - Event payload. Must include `eventName` when sending to GA.
     */
    transmitEvent(providers = [], payload = {}) {
      return sendEvent(providers, payload, this.$store)
    },
  },
}
